(() => {
  // Shopify menu links keep a working Support destination when chat is blocked.
  document.addEventListener('click', async (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('a[href]');
    if (!link || (link.target && link.target !== '_self')) return;
    const destination = new URL(link.href, window.location.href);
    if (destination.origin !== window.location.origin || destination.hash !== '#live-chat') return;
    event.preventDefault();

    let timer, loaded;
    try {
      await Promise.race([
        new Promise((resolve) => {
          loaded = resolve;
          if (window.GorgiasChat) resolve();
          else window.addEventListener('gorgias-widget-loaded', loaded, { once: true });
        }).then(() => window.GorgiasChat.init()),
        new Promise((_, reject) => { timer = window.setTimeout(() => reject(new Error('Chat unavailable')), 10000); }),
      ]);
      const menu = link.closest('drawer-nav');
      if (menu?.isOpen) menu.querySelector('.wt-drawer__close')?.click();
      window.GorgiasChat.open();
    } catch {
      window.location.assign(destination.href);
    } finally {
      window.clearTimeout(timer);
      window.removeEventListener('gorgias-widget-loaded', loaded);
    }
  });

  const updateSonicPaymentTerms = (variant) => {
    if (!variant?.id) return;

    document.querySelectorAll("[data-sonic-payment-terms]").forEach((terms) => {
      const label = terms.querySelector("[data-sonic-payment-terms-label]");
      const amountsScript = terms.querySelector("[data-sonic-payment-terms-amounts]");
      const template = terms.dataset.labelTemplate;

      if (!label || !amountsScript || !template) return;

      try {
        const amounts = JSON.parse(amountsScript.textContent || "{}");
        const amount = amounts[String(variant.id)];

        if (amount) {
          label.innerHTML = template.replace("[[amount]]", amount);
        }
      } catch (error) {
        // Keep the server-rendered fallback if the JSON cannot be parsed.
      }
    });
  };

  const updateSonicBuyboxSummary = (variant) => {
    if (!variant?.id) return;

    document.querySelectorAll("[data-sonic-buybox-summary]").forEach((summary) => {
      const variantsScript = summary.querySelector("[data-sonic-buybox-summary-variants]");
      if (!variantsScript) return;

      try {
        const variants = JSON.parse(variantsScript.textContent || "{}");
        let variantData = variants[String(variant.id)];
        if (!variantData) return;
        const sellingPlan = Array.from(summary.closest('form')?.elements || [])
          .find((input) => input.name === 'selling_plan');
        if (sellingPlan && !sellingPlan.disabled) {
          variantData = variantData.sellingPlans?.[sellingPlan.value] || variantData;
        }
        const selectedPlanId = sellingPlan && !sellingPlan.disabled ? sellingPlan.value : '';
        summary.closest('.wt-product__info')?.querySelectorAll('[data-sonic-payment-messages]').forEach((messages) => {
          const options = Array.from(messages.querySelectorAll('[data-sonic-payment-option]'));
          const selected = options.some(option => option.dataset.sonicPaymentOption === selectedPlanId)
            ? selectedPlanId : (selectedPlanId ? 'subscription' : '');
          options.forEach(option => option.setAttribute('aria-hidden', String(option.dataset.sonicPaymentOption !== selected)));
        });

        const current = summary.querySelector("[data-sonic-buybox-summary-current]");
        const compare = summary.querySelector("[data-sonic-buybox-summary-compare]");
        const badge = summary.querySelector("[data-sonic-buybox-summary-badge]");
        const termsLabel = summary.querySelector("[data-sonic-buybox-summary-terms-label]");
        const template = summary.dataset.paymentLabelTemplate;
        const form = summary.closest("form");
        const buttonLabel = form?.querySelector("[data-sonic-buybox-atc-label]");

        if (current) current.textContent = variantData.price;
        if (compare) {
          compare.textContent = variantData.compareAtPrice;
          compare.classList.toggle("is-hidden", !variantData.hasCompareAtPrice);
        }
        if (badge) {
          badge.textContent = variantData.savingLabel;
          badge.classList.toggle("is-hidden", !variantData.hasCompareAtPrice);
        }
        if (termsLabel && template && variantData.installmentAmount) {
          termsLabel.innerHTML = template.replace("[[amount]]", variantData.installmentAmount);
        }
        if (sellingPlan) {
          const terms = form.closest('.wt-product__info')?.querySelector('[data-sonic-payment-terms]');
          const label = terms?.querySelector('[data-sonic-payment-terms-label]');
          if (label && terms.dataset.labelTemplate) {
            label.innerHTML = terms.dataset.labelTemplate.replace('[[amount]]', variantData.installmentAmount);
          }
        }
        if (buttonLabel && variantData.buttonLabel && !buttonLabel.closest("button")?.disabled) {
          buttonLabel.textContent = variantData.buttonLabel;
        }
      } catch (error) {
        // Keep the server-rendered values if variant summary JSON cannot be parsed.
      }
    });
  };

  const initSonicApplianceRotators = (scope = document) => {
    scope.querySelectorAll("[data-sonic-appliance-rotator]").forEach((rotator) => {
      if (rotator.dataset.rotatorReady === "true") return;

      const items = Array.from(rotator.querySelectorAll("[data-sonic-appliance-rotator-item]"));

      if (items.length <= 1) return;

      rotator.dataset.rotatorReady = "true";

      let activeIndex = Math.max(
        0,
        items.findIndex((item) => item.classList.contains("is-active"))
      );
      const interval = Number(rotator.dataset.interval) || 3000;
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

      const setActiveState = (nextIndex) => {
        items.forEach((item, index) => {
          item.classList.toggle("is-active", index === nextIndex);
          item.setAttribute("aria-hidden", index === nextIndex ? "false" : "true");
        });
        activeIndex = nextIndex;
      };

      const startFallback = () => {
        setActiveState(activeIndex);

        window.setInterval(() => {
          setActiveState((activeIndex + 1) % items.length);
        }, interval);
      };

      if (reduceMotion.matches || !window.matchMedia('(hover: hover) and (pointer: fine)').matches || !window.sonicLoadGSAP) {
        startFallback();
        return;
      }

      window
        .sonicLoadGSAP()
        .then(({ gsap }) => {
          if (!gsap || !document.body.contains(rotator)) {
            startFallback();
            return;
          }

          const rotatorWrapper = rotator.closest(".wt-product__appliance-rotator") || rotator;

          rotator.classList.add("is-gsap-ready");
          rotatorWrapper.classList.add("is-gsap-ready");
          setActiveState(activeIndex);

          gsap.set(items, {
            autoAlpha: 0,
            y: 16,
            scale: 0.985,
            filter: "blur(5px)",
          });
          gsap.set(items[activeIndex], {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            filter: "blur(0px)",
          });

          window.setInterval(() => {
            const currentItem = items[activeIndex];
            const nextIndex = (activeIndex + 1) % items.length;
            const nextItem = items[nextIndex];

            if (rotator.sonicApplianceTimeline) {
              rotator.sonicApplianceTimeline.kill();
            }

            currentItem.classList.remove("is-active");
            currentItem.setAttribute("aria-hidden", "true");
            nextItem.classList.add("is-active");
            nextItem.setAttribute("aria-hidden", "false");

            rotator.sonicApplianceTimeline = gsap.timeline({
              defaults: { ease: "power3.out" },
              onComplete: () => {
                activeIndex = nextIndex;
              },
            });

            rotator.sonicApplianceTimeline
              .to(currentItem, {
                autoAlpha: 0,
                y: -14,
                scale: 0.985,
                filter: "blur(5px)",
                duration: 0.36,
                ease: "power2.inOut",
              })
              .fromTo(
                nextItem,
                {
                  autoAlpha: 0,
                  y: 18,
                  scale: 0.965,
                  filter: "blur(7px)",
                },
                {
                  autoAlpha: 1,
                  y: 0,
                  scale: 1,
                  filter: "blur(0px)",
                  duration: 0.72,
                  ease: "power4.out",
                },
                0.1
              )
              .fromTo(
                nextItem.querySelector(".wt-product__appliance-rotator-icon"),
                { y: 6, scale: 0.92, rotate: -2 },
                { y: 0, scale: 1, rotate: 0, duration: 0.72, ease: "back.out(1.35)" },
                0.14
              );
          }, interval);
        })
        .catch(startFallback);
    });
  };

  const isSonicProductTemplate = () => Boolean(document.querySelector("[data-sonic-product-template]"));
  const initSonicProductSticky = () => {
    if (!isSonicProductTemplate()) return;

    const product = document.querySelector(".wt-product");
    const gallery = document.querySelector(".wt-product__gallery");
    const main = document.querySelector(".wt-product__main");
    const header = document.querySelector(".page-header");
    const productDock = document.querySelector(".wt-featured-nav--product-dock")?.closest(".wt-featured-nav");

    if (!product || !gallery || !main) return;
    if (main.dataset.sonicStickyReady === "true") return;

    main.dataset.sonicStickyReady = "true";

    const desktopQuery = window.matchMedia("(min-width: 900px)");
    let frame = null;
    let lastTop = null;
    let lastDisabled = null;
    let lastDockStuck = null;
    let lastScrollY = window.pageYOffset;
    let scrollDirection = "none";

    const getHeaderOffset = () => {
      if (!header) return 0;
      const isShowing = header.classList.contains("sticky-enabled") && header.classList.contains("sticky-show");
      return isShowing && scrollDirection !== "down" ? header.offsetHeight : 0;
    };

    const update = () => {
      frame = null;

      const baseTop = 16;
      const stickyTop = baseTop + (desktopQuery.matches ? getHeaderOffset() : 0);
      const shouldDisable = !desktopQuery.matches || gallery.offsetHeight <= main.offsetHeight + 120;
      const dockStuck = Boolean(productDock && window.pageYOffset > 0 && productDock.getBoundingClientRect().top <= 0.5);

      if (lastTop !== stickyTop) {
        main.style.setProperty("--sonic-product-sticky-top", `${stickyTop}px`);
        lastTop = stickyTop;
      }

      if (lastDisabled !== shouldDisable) {
        document.body.classList.toggle("sonic-product-sticky-disabled", shouldDisable);
        lastDisabled = shouldDisable;
      }

      if (productDock && lastDockStuck !== dockStuck) {
        productDock.classList.toggle("is-stuck", dockStuck);
        lastDockStuck = dockStuck;
      }
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    const handleScroll = () => {
      const currentScrollY = window.pageYOffset;

      if (currentScrollY > lastScrollY + 2) {
        scrollDirection = "down";
      } else if (currentScrollY < lastScrollY - 2) {
        scrollDirection = "up";
      }

      lastScrollY = currentScrollY;
      requestUpdate();
    };

    const resizeObserver = "ResizeObserver" in window ? new ResizeObserver(requestUpdate) : null;
    resizeObserver?.observe(main);
    resizeObserver?.observe(gallery);

    const mutationObserver = header && "MutationObserver" in window
      ? new MutationObserver(requestUpdate)
      : null;
    mutationObserver?.observe(header, { attributes: true, attributeFilter: ["class"] });

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", requestUpdate, { passive: true });
    desktopQuery.addEventListener?.("change", requestUpdate);

    update();
  };

  const initSonicProductDockNav = () => {
    if (!isSonicProductTemplate()) return;

    const dock = document.querySelector(".wt-featured-nav--product-dock")?.closest(".wt-featured-nav");
    if (!dock || dock.dataset.sonicDockNavReady === "true") return;

    const hashLinks = Array.from(dock.querySelectorAll('a[href^="#"]')).filter((link) => link.hash.length > 1);
    const links = hashLinks.filter((link) => link.classList.contains("secondary-navigation__link"));
    if (!hashLinks.length) return;

    dock.dataset.sonicDockNavReady = "true";

    const header = document.querySelector(".page-header");
    const decodeHash = (hash) => {
      try {
        return decodeURIComponent(hash.replace(/^#/, ""));
      } catch (error) {
        return hash.replace(/^#/, "");
      }
    };

    const getHeaderOffset = () => {
      const headerHeight =
        header?.classList.contains("sticky-show") || header?.classList.contains("sticky-enabled")
          ? header.offsetHeight
          : 0;

      return headerHeight + dock.offsetHeight + 16;
    };

    const findTarget = (anchor) => {
      const directTarget = document.getElementById(anchor);
      if (directTarget) return directTarget;

      return document.querySelector(`[data-sonic-anchor="${anchor}"]`);
    };

    const navItems = links
      .map((link) => {
        const anchor = decodeHash(link.hash);
        const target = findTarget(anchor);
        return target ? { link, anchor, target } : null;
      })
      .filter(Boolean);

    if (!navItems.length) return;

    let activeLink = null;
    const setActive = (activeItem) => {
      if (activeLink === activeItem.link) return;
      activeLink = activeItem.link;
      navItems.forEach((item) => {
        const isActive = item.link === activeLink;
        item.link.classList.toggle("secondary-navigation__link--active", isActive);
        item.link.closest(".secondary-navigation__swiper-slide")?.classList.toggle("secondary-navigation__swiper-slide--active", isActive);

        if (isActive) {
          item.link.setAttribute("aria-current", "true");
        } else {
          item.link.removeAttribute("aria-current");
        }
      });
    };

    const scrollToTarget = (item) => {
      const top = item.target.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset();
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      setActive(item);

      if (history.pushState) {
        history.pushState(null, "", `#${item.anchor}`);
      }
    };

    hashLinks.forEach((link) => {
      const anchor = decodeHash(link.hash);
      const target = findTarget(anchor);
      if (!target) return;

      const item = { link, anchor, target };
      item.link.addEventListener("click", (event) => {
        event.preventDefault();
        scrollToTarget(item);
      });
    });

    let frame = null;
    const updateActive = () => {
      frame = null;
      const offset = getHeaderOffset() + 8;
      const isAtPageEnd = window.innerHeight + window.pageYOffset >= document.documentElement.scrollHeight - 24;
      const current = isAtPageEnd ? navItems[navItems.length - 1] : navItems.reduce((closest, item) => {
        const top = item.target.getBoundingClientRect().top;
        const distance = Math.abs(top - offset);

        if (top > window.innerHeight * 0.75 && item !== navItems[0]) {
          return closest;
        }

        return distance < closest.distance ? { item, distance } : closest;
      }, { item: navItems[0], distance: Infinity }).item;

      setActive(current);
    };

    const requestActiveUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(updateActive);
    };

    window.addEventListener("scroll", requestActiveUpdate, { passive: true });
    window.addEventListener("resize", requestActiveUpdate, { passive: true });
    updateActive();

    if (window.location.hash) {
      const initialAnchor = decodeHash(window.location.hash);
      const initialItem = navItems.find((item) => item.anchor === initialAnchor);
      if (initialItem) {
        window.requestAnimationFrame(() => scrollToTarget(initialItem));
      }
    }
  };

  const initSonicCustom = (scope = document) => {
    initSonicApplianceRotators(scope);
    initSonicProductSticky();
    initSonicProductDockNav();
  };

  document.addEventListener("DOMContentLoaded", () => {
    initSonicCustom();

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        updateSonicPaymentTerms(event?.data?.variant);
        updateSonicBuyboxSummary(event?.data?.variant);
      });
    }
  });

  document.addEventListener('sonic:purchase-option-change', (event) => {
    updateSonicBuyboxSummary({ id: event.detail.variantId });
  });

  document.addEventListener("shopify:section:load", (event) => {
    initSonicCustom(event.target);
  });
})();
